import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, articles, featuredArticles, homeFAQ, stats } from '@/mocks/articles';

export default function Home() {
  const featuredData = featuredArticles
    .map((id) => articles.find((a) => a.id === id))
    .filter(Boolean) as typeof articles;

  const latestArticles = [...articles].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  ).slice(0, 6);

  const homeSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: '佐山爱 — 官方资料与作品全览',
      description: '全面介绍佐山爱的个人资料、出道经历、代表作品及最新动态。',
      url: 'https://yyloviest.cn/',
      isPartOf: {
        '@type': 'WebSite',
        name: '佐山爱官方资料站',
        url: 'https://yyloviest.cn/',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: homeFAQ.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    },
  ];

  return (
    <>
      <head>
        <title>佐山爱 — 官方资料与作品全览 | 个人资料、出道经历、代表作品</title>
        <meta name="description" content="全面介绍佐山爱的个人资料、出道经历、代表作品及最新动态。无论你是初次了解佐山爱还是资深粉丝，这里汇聚最权威、最完整的佐山爱相关信息，带你深入认识这位备受关注的日本女艺人。" />
        <meta name="keywords" content="佐山爱,佐山爱作品,佐山爱个人资料,佐山爱出道经历,佐山爱最新动态,佐山爱粉丝社区,佐山爱写真" />
        <link rel="canonical" href="https://yyloviest.cn/" />
        <meta property="og:title" content="佐山爱 — 官方资料与作品全览" />
        <meta property="og:description" content="全面介绍佐山爱的个人资料、出道经历、代表作品及最新动态。汇聚最权威、最完整的佐山爱相关信息。" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yyloviest.cn/" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="佐山爱 — 官方资料与作品全览" />
        <meta name="twitter:description" content="全面介绍佐山爱的个人资料、出道经历、代表作品及最新动态。" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(homeSchema) }}
        />
      </head>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full min-h-[520px] md:min-h-[620px] flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20Japanese%20minimalist%20artistic%20composition%20with%20soft%20warm%20gradient%20from%20cream%20to%20soft%20amber%2C%20delicate%20cherry%20blossom%20petals%20floating%20gently%2C%20subtle%20brush%20stroke%20textures%2C%20zen-inspired%20negative%20space%2C%20ethereal%20atmospheric%20lighting%2C%20sophisticated%20gallery-grade%20aesthetic%2C%20no%20people%2C%20warm%20neutral%20tones%20with%20hints%20of%20rose%20gold&width=1800&height=900&seq=home-hero-sayama&orientation=landscape"
                alt="佐山爱官方资料站"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/45"></div>
            </div>

            <div className="relative z-10 w-full text-center px-4 md:px-6 py-20">
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-wide leading-tight">
                佐山爱
                <span className="block text-lg md:text-2xl font-body font-normal mt-3 text-white/80 tracking-wider">
                  官方资料与作品全览
                </span>
              </h1>
              <p className="max-w-2xl mx-auto text-sm md:text-base text-white/70 leading-relaxed mb-8">
                全面介绍佐山爱的个人资料、出道经历、代表作品及最新动态。无论你是初次了解佐山爱还是资深粉丝，这里汇聚最权威、最完整的佐山爱相关信息，带你深入认识这位备受关注的日本女艺人。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category"
                  className="px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  浏览全部作品
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
                <Link
                  to="/about"
                  className="px-6 py-3 bg-white/15 text-white backdrop-blur-sm rounded-full text-sm font-medium hover:bg-white/25 transition-all duration-200 whitespace-nowrap cursor-pointer border border-white/20"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </section>

          {/* Stats Section */}
          <section className="relative z-10 -mt-12 max-w-5xl mx-auto px-4 md:px-6">
            <div className="bg-background-50 rounded-2xl border border-background-200/70 grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-background-200/70 overflow-hidden">
              {stats.map((stat) => (
                <div key={stat.label} className="p-6 md:p-8 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100/70">
                    <i className={`${stat.icon} text-primary-600 text-lg`}></i>
                  </div>
                  <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                    {stat.value}
                  </div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Category Navigation */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 mt-16 md:mt-20">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950">
                内容分类
              </h2>
              <p className="text-sm text-foreground-500 mt-2">
                按分类浏览，快速找到你感兴趣的佐山爱相关内容
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={cat.id === 'all' ? '/category' : `/category?tab=${cat.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-200 cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100/70 text-accent-600 mb-3 group-hover:bg-accent-200/70 transition-colors duration-200">
                    <i className={`${cat.icon} text-lg`}></i>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-1 whitespace-nowrap">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">
                    {cat.description}
                  </p>
                  <span className="inline-block mt-3 text-xs text-primary-600 font-medium whitespace-nowrap">
                    {cat.count} 篇内容
                  </span>
                </Link>
              ))}
            </div>
          </section>

          {/* Featured Articles */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 mt-16 md:mt-20">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950">
                  精选文章
                </h2>
                <p className="text-sm text-foreground-500 mt-1">编辑推荐的高质量内容</p>
              </div>
              <Link
                to="/category"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
              >
                查看全部 <i className="ri-arrow-right-line ml-1 text-xs"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
              {featuredData.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-300 transition-all duration-200 cursor-pointer"
                >
                  <div className="flex flex-col sm:flex-row">
                    <div className="w-full sm:w-48 h-40 sm:h-auto flex-shrink-0 overflow-hidden">
                      <img
                        src={article.cardImage}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5 flex flex-col justify-between flex-1">
                      <div>
                        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700 mb-2 whitespace-nowrap">
                          {article.categoryName}
                        </span>
                        <h3 className="font-heading text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors duration-150 line-clamp-2 mb-2">
                          {article.title}
                        </h3>
                        <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">
                          {article.summary}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line"></i> {article.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i> {article.readTime} 分钟阅读
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* Latest Articles */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 mt-16 md:mt-20">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950">
                  最新动态
                </h2>
                <p className="text-sm text-foreground-500 mt-1">第一时间了解佐山爱的最新资讯</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {latestArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-300 transition-all duration-200 cursor-pointer"
                >
                  <div className="h-44 overflow-hidden">
                    <img
                      src={article.cardImage}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700 mb-2 whitespace-nowrap">
                      {article.categoryName}
                    </span>
                    <h3 className="font-heading text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors duration-150 line-clamp-2 mb-2">
                      {article.title}
                    </h3>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i> {article.date}
                      </span>
                      <span>{article.readTime} 分钟阅读</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* FAQ Section */}
          <section className="max-w-4xl mx-auto px-4 md:px-6 mt-16 md:mt-20 mb-16 md:mb-20">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500 mt-2">关于佐山爱，你可能想了解的问题</p>
            </div>

            <div className="space-y-3">
              {homeFAQ.map((faq, index) => (
                <details
                  key={index}
                  className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden"
                >
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100/70 transition-colors">
                    <span className="font-heading text-sm font-medium text-foreground-950 pr-4">
                      {faq.question}
                    </span>
                    <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center text-foreground-400 group-open:hidden">
                      <i className="ri-add-line text-sm"></i>
                    </span>
                    <span className="w-5 h-5 flex-shrink-0 hidden group-open:flex items-center justify-center text-foreground-400">
                      <i className="ri-subtract-line text-sm"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-accent-500">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-14 md:py-18 text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-background-50 mb-3">
                加入佐山爱粉丝社区
              </h2>
              <p className="text-sm text-background-50/80 mb-8 max-w-xl mx-auto leading-relaxed">
                与全球粉丝一起分享关于佐山爱的点滴，获取最新的独家内容和社区活动信息
              </p>
              <Link
                to="/category?tab=community"
                className="inline-flex items-center px-6 py-3 bg-background-50 text-accent-600 rounded-full text-sm font-medium hover:bg-background-100 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                探索社区 <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}